import { useEffect, useState } from "react";
import { Plus, Download, FlaskConical, Stethoscope, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { api, formatError } from "../lib/api";
import { generateExamOrderPDF } from "../lib/examPdf";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { FileUploader, AttachmentList } from "../components/FileUploader";
import MedicationCombobox from "../components/MedicationCombobox";

export default function Exams() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState({ lab: [], imaging: [] });
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    patient_id: "", type: "lab", urgency: "routine",
    clinical_info: "", notes: "", tests: [{ name: "", notes: "" }],
  });

  const load = () => api.get("/exam-orders")
    .then(({ data }) => setItems(data || []))
    .catch((err) => toast.error(formatError(err)));

  useEffect(() => {
    load();
    api.get("/exams/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
    if (user?.role === "doctor" || user?.role === "admin") {
      // Prefer the canonical /patients list (doctor+admin authorized). Fallback to appointments.
      api.get("/patients")
        .then(({ data }) => setPatients((data || []).map((p) => ({ id: p.id, name: p.name }))))
        .catch(() => {
          api.get("/appointments").then(({ data }) => {
            const map = {};
            (data || []).forEach((a) => { map[a.patient_id] = a.patient_name; });
            setPatients(Object.entries(map).map(([id, name]) => ({ id, name })));
          }).catch(() => {});
        });
    }
  }, [user]);

  const setTest = (i, k, v) => setForm((p) => {
    const tests = [...p.tests]; tests[i] = { ...tests[i], [k]: v }; return { ...p, tests };
  });
  const addTest = () => setForm((p) => ({ ...p, tests: [...p.tests, { name: "", notes: "" }] }));
  const removeTest = (i) => setForm((p) => ({ ...p, tests: p.tests.filter((_, j) => j !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    const tests = form.tests.filter((tt) => tt.name && tt.name.trim());
    if (!form.patient_id || tests.length === 0) {
      toast.error(t("error_generic"));
      return;
    }
    try {
      const { data } = await api.post("/exam-orders", { ...form, tests });
      toast.success(t("saved"));
      setOpen(false);
      setForm({ patient_id: "", type: "lab", urgency: "routine", clinical_info: "", notes: "", tests: [{ name: "", notes: "" }] });
      load();
      generateExamOrderPDF(data);
    } catch (err) { toast.error(formatError(err)); }
  };

  const list = form.type === "lab" ? catalog.lab : catalog.imaging;
  const Icon = (type) => (type === "lab" ? FlaskConical : Stethoscope);

  const uploadResults = async (orderId, files) => {
    if (!files.length) return false;
    try {
      await api.post(`/exam-orders/${orderId}/results`, { file_ids: files.map((f) => f.id) });
      toast.success("Resultados subidos");
      load();
      return true;
    } catch (err) { toast.error(formatError(err)); return false; }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="exams-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-3xl">{t("exams")}</h1>
        {user?.role === "doctor" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="new-exam-btn"><Plus className="w-4 h-4 mr-2" /> {t("new_exam")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t("new_exam")}</DialogTitle>
                <DialogDescription>Solicitud de laboratorio o estudio de imagen para el paciente.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("type")}</Label>
                    <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v, tests: [{ name: "", notes: "" }] }))}>
                      <SelectTrigger data-testid="exam-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lab">{t("exam_lab")}</SelectItem>
                        <SelectItem value="imaging">{t("exam_imaging")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("urgency")}</Label>
                    <Select value={form.urgency} onValueChange={(v) => setForm((p) => ({ ...p, urgency: v }))}>
                      <SelectTrigger data-testid="exam-urgency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="routine">{t("routine")}</SelectItem>
                        <SelectItem value="preferent">{t("preferent")}</SelectItem>
                        <SelectItem value="urgent">{t("urgent")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>{t("patient")}</Label>
                  <Select value={form.patient_id} onValueChange={(v) => setForm((p) => ({ ...p, patient_id: v }))}>
                    <SelectTrigger data-testid="exam-patient"><SelectValue placeholder={t("select_patient")} /></SelectTrigger>
                    <SelectContent>
                      {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("clinical_info")}</Label>
                  <Textarea rows={2} value={form.clinical_info} onChange={(e) => setForm((p) => ({ ...p, clinical_info: e.target.value }))} data-testid="exam-clinical" />
                </div>
                <div className="space-y-2">
                  <Label>Pruebas</Label>
                  {form.tests.map((tt, i) => (
                    <div key={i} className="border border-border rounded-lg p-3 space-y-2">
                      <div className="flex gap-2">
                        <MedicationCombobox
                          items={list}
                          value={tt.name}
                          onSelect={(item) => setTest(i, "name", typeof item === "string" ? item : item.name)}
                          placeholder="Selecciona del catálogo (con búsqueda)"
                          testid={`exam-test-${i}-select`}
                        />
                        {form.tests.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeTest(i)} data-testid={`exam-test-${i}-remove`}><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                      <Input placeholder={t("custom_test")} value={tt.name} onChange={(e) => setTest(i, "name", e.target.value)} data-testid={`exam-test-${i}-name`} />
                      <Input placeholder={t("instructions")} value={tt.notes} onChange={(e) => setTest(i, "notes", e.target.value)} data-testid={`exam-test-${i}-notes`} />
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addTest} data-testid="exam-test-add"><Plus className="w-4 h-4 mr-1" /> {t("add_test")}</Button>
                </div>
                <div>
                  <Label>{t("notes")}</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="exam-notes" />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" data-testid="exam-save-btn">{t("save")}</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display">{t("exams")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {items.length === 0 && <div className="text-sm text-muted-foreground">{t("no_data")}</div>}
          {items.map((o) => {
            const I = Icon(o.type);
            return (
              <div key={o.id} className="border border-border rounded-lg p-4 space-y-3" data-testid={`exam-${o.id}`}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#F3F1EC] flex items-center justify-center"><I className="w-5 h-5 text-primary" /></div>
                    <div>
                      <div className="text-xs text-muted-foreground">{o.code} · {new Date(o.created_at).toLocaleDateString()}</div>
                      <div className="font-medium">{t(`exam_${o.type}`)} · {o.tests.length} prueba{o.tests.length !== 1 && "s"}</div>
                      <div className="text-xs text-muted-foreground">{user?.role === "patient" ? o.doctor_name : o.patient_name}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={o.urgency === "urgent" ? "destructive" : "outline"}>{t(o.urgency)}</Badge>
                    <Button variant="outline" size="sm" onClick={async () => generateExamOrderPDF(o)} data-testid={`exam-pdf-${o.id}`}>
                      <Download className="w-4 h-4 mr-1" /> {t("download_pdf")}
                    </Button>
                  </div>
                </div>
                {(o.results && o.results.length > 0) && (
                  <div className="bg-[#F3F1EC] rounded-lg p-3">
                    <div className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                      <Upload className="w-3 h-3" /> Resultados subidos
                    </div>
                    <AttachmentList items={o.results} />
                  </div>
                )}
                <ResultsUploader order={o} onUpload={uploadResults} />
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function ResultsUploader({ order, onUpload }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const submit = async () => {
    setUploading(true);
    const ok = await onUpload(order.id, files);
    setUploading(false);
    if (ok) setFiles([]);
  };
  return (
    <div className="border-t border-border pt-3">
      <div className="text-xs text-muted-foreground mb-2">Subir resultados de esta petición</div>
      <FileUploader files={files} setFiles={setFiles} purpose="exam-results" testid={`exam-${order.id}-results`} />
      {files.length > 0 && (
        <Button
          size="sm"
          className="bg-primary hover:bg-primary/90 mt-2"
          onClick={submit}
          disabled={uploading}
          data-testid={`exam-${order.id}-upload-btn`}
        >
          <Upload className="w-4 h-4 mr-1" /> {uploading ? "Subiendo..." : "Adjuntar a la petición"}
        </Button>
      )}
    </div>
  );
}
